import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { toast } from "sonner";
import { GraduationCap, Briefcase, Download, ArrowLeft, Search, PencilLine } from "lucide-react";

const STUDY_COLORS = {
  elite: "#7C3AED",       // ungu — elite
  ptn: "#1E3A8A",         // navy — PTN Indonesia
  luar_negeri: "#0EA5E9", // biru langit — LN non-elite
  pts_unknown: "#9CA3AF", // grey — PTS / tidak diketahui
};

const CAREER_COLORS = {
  pengusaha: "#E07A5F",   // coral
  profesional: "#1E3A8A", // navy
  freelancer: "#F59E0B",  // amber
  lainnya: "#9CA3AF",
};

export default function AlumniAnalytics() {
  const { user } = useAuth();
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("__all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // { review_id, ...fields }
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = "Analitik Alumni — Admin";
    (async () => {
      try {
        const r = await api.get("/schools?limit=500");
        setSchools(r.data?.items || []);
      } catch (_) {
        toast.error("Gagal memuat daftar sekolah");
      }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (schoolId && schoolId !== "__all") params.set("school_id", schoolId);
      const r = await api.get(`/admin/alumni-analytics?${params.toString()}`);
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memuat analitik alumni");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  const filteredList = useMemo(() => {
    if (!data) return [];
    if (!q.trim()) return data.alumni_list;
    const needle = q.trim().toLowerCase();
    return (data.alumni_list || []).filter((a) => {
      const hay = [
        a.user_name, a.school_name, a.school_city,
        a.alumni_edu_d3, a.alumni_edu_s1, a.alumni_edu_s2, a.alumni_edu_s3,
        a.alumni_next_study, a.alumni_current_profession,
        a.alumni_current_company, a.alumni_previous_company,
        a.elite_university, String(a.graduation_year || ""),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [data, q]);

  const downloadCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (schoolId && schoolId !== "__all") params.set("school_id", schoolId);
      const r = await api.get(`/admin/alumni-analytics.csv?${params.toString()}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `alumni-analytics-${schoolId === "__all" ? "all" : schoolId}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV terunduh");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal unduh CSV");
    }
  };

  const savePatch = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const { review_id, ...rest } = editing;
      const body = {};
      for (const k of Object.keys(rest)) {
        if (rest[k] !== undefined) body[k] = rest[k];
      }
      await api.patch(`/admin/alumni-analytics/${review_id}`, body);
      toast.success("Data dirapikan");
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  if (!user || !["admin", "superadmin", "moderator"].includes(user.role)) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center">
        <p className="text-[#4B5563]">Halaman ini hanya untuk admin & moderator.</p>
        <Link to="/" className="text-[#1E3A8A] underline">Kembali ke beranda</Link>
      </div>
    );
  }

  return (
    <div data-testid="alumni-analytics-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12 space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <Link to="/admin" className="text-xs text-[#4B5563] hover:text-[#1E3A8A] inline-flex items-center gap-1">
            <ArrowLeft size={12} /> Kembali ke Dasbor Admin
          </Link>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-[#1F2937] mt-1">Analitik Alumni</h1>
          <p className="text-sm text-[#4B5563] mt-1">Data lanjutan studi, profesi, &amp; tren karir dari seluruh alumni yang menulis review.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={downloadCsv} data-testid="alumni-export-csv" className="rounded-full h-10 gap-2">
            <Download size={14} /> Ekspor CSV
          </Button>
        </div>
      </div>

      {/* Filter row */}
      <div className="bg-white border border-[#EAE7E0] rounded-2xl p-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1.5">Filter sekolah</div>
          <Select value={schoolId} onValueChange={setSchoolId}>
            <SelectTrigger className="rounded-xl h-10" data-testid="alumni-school-filter">
              <SelectValue placeholder="Semua sekolah" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Semua sekolah</SelectItem>
              {schools.map((s) => (
                <SelectItem key={s.school_id} value={s.school_id}>{s.name} · {s.city}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1.5">Cari dalam tabel</div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Nama, universitas, profesi, perusahaan…"
              data-testid="alumni-search" className="rounded-xl h-10 pl-9" />
          </div>
        </div>
      </div>

      {/* KPIs */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Total alumni" value={data.total_alumni} testid="kpi-total" />
          <KpiCard label="Alumni Elite (Top 50)" value={data.elite_count}
            sub={data.total_alumni ? `${((data.elite_count / data.total_alumni) * 100).toFixed(1)}%` : "0%"}
            accent="#7C3AED" testid="kpi-elite" />
          <KpiCard label="Pengusaha"
            value={(data.career_trends || []).find((c) => c.key === "pengusaha")?.count || 0}
            sub={`${(data.career_trends || []).find((c) => c.key === "pengusaha")?.pct || 0}%`}
            accent="#E07A5F" testid="kpi-pengusaha" />
          <KpiCard label="Karyawan Profesional"
            value={(data.career_trends || []).find((c) => c.key === "profesional")?.count || 0}
            sub={`${(data.career_trends || []).find((c) => c.key === "profesional")?.pct || 0}%`}
            accent="#1E3A8A" testid="kpi-profesional" />
        </div>
      )}

      {/* Charts */}
      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border border-[#EAE7E0] rounded-2xl p-5" data-testid="chart-study-stats">
            <div className="flex items-center gap-2 mb-3">
              <GraduationCap size={18} className="text-[#1E3A8A]" />
              <h3 className="font-serif-display text-xl text-[#1F2937]">Tujuan kuliah alumni</h3>
            </div>
            {(data.study_stats || []).every((s) => s.count === 0) ? (
              <div className="text-sm text-[#4B5563] italic py-8 text-center">Belum ada data.</div>
            ) : (
              <div style={{ width: "100%", height: 280, minHeight: 280 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={data.study_stats} dataKey="count" nameKey="label"
                      cx="50%" cy="50%" outerRadius={90} label={(e) => `${e.pct}%`}>
                      {(data.study_stats || []).map((s) => (
                        <Cell key={s.key} fill={STUDY_COLORS[s.key] || "#9CA3AF"} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="bg-white border border-[#EAE7E0] rounded-2xl p-5" data-testid="chart-career-trends">
            <div className="flex items-center gap-2 mb-3">
              <Briefcase size={18} className="text-[#E07A5F]" />
              <h3 className="font-serif-display text-xl text-[#1F2937]">Tren karir alumni</h3>
            </div>
            {(data.career_trends || []).every((c) => c.count === 0) ? (
              <div className="text-sm text-[#4B5563] italic py-8 text-center">Belum ada data.</div>
            ) : (
              <div style={{ width: "100%", height: 280, minHeight: 280 }}>
                <ResponsiveContainer>
                  <BarChart data={data.career_trends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EAE7E0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                      {(data.career_trends || []).map((c) => (
                        <Cell key={c.key} fill={CAREER_COLORS[c.key] || "#9CA3AF"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Alumni table */}
      <div className="bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[#EAE7E0] flex items-center justify-between">
          <h3 className="font-semibold text-[#1F2937]">Daftar alumni {data ? `(${filteredList.length}${q ? ` / ${data.total_alumni}` : ""})` : ""}</h3>
          {loading && <span className="text-xs text-[#4B5563] italic">Memuat…</span>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="alumni-table">
            <thead className="bg-[#FAFAF8] border-b border-[#EAE7E0]">
              <tr className="text-left text-xs uppercase tracking-widest text-[#4B5563]">
                <th className="px-4 py-3 min-w-[140px]">Nama</th>
                <th className="px-4 py-3 min-w-[180px]">Sekolah</th>
                <th className="px-4 py-3">Tahun lulus</th>
                <th className="px-4 py-3 min-w-[200px]">Studi lanjutan</th>
                <th className="px-4 py-3 min-w-[200px]">Profesi &amp; perusahaan</th>
                <th className="px-4 py-3">Tujuan</th>
                <th className="px-4 py-3">Karir</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filteredList.map((a, i) => {
                const eduLines = [];
                if (a.alumni_edu_s3) eduLines.push(`S3: ${a.alumni_edu_s3}`);
                if (a.alumni_edu_s2) eduLines.push(`S2: ${a.alumni_edu_s2}`);
                if (a.alumni_edu_s1) eduLines.push(`S1: ${a.alumni_edu_s1}`);
                if (a.alumni_edu_d3) eduLines.push(`D3: ${a.alumni_edu_d3}`);
                if (!eduLines.length && a.alumni_next_study) eduLines.push(a.alumni_next_study);
                return (
                  <tr key={a.review_id} data-testid={`alumni-row-${i}`}
                    className="border-b border-[#EAE7E0] last:border-b-0 hover:bg-[#FAFAF8] align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#1F2937]">{a.user_name || "—"}</div>
                      {a.is_anonymous && (
                        <div className="text-[10px] mt-0.5 inline-block px-1.5 py-0.5 rounded bg-[#EFF6FF] text-[#1E3A8A]">Anonim di publik</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-[#1F2937]">{a.school_name || "—"}</div>
                      <div className="text-xs text-[#4B5563]">{a.school_city}</div>
                    </td>
                    <td className="px-4 py-3">{a.graduation_year || "—"}</td>
                    <td className="px-4 py-3">
                      {eduLines.length ? (
                        <div className="space-y-0.5">
                          {eduLines.map((l, j) => <div key={j} className="text-[#1F2937]">{l}</div>)}
                          {a.is_elite_alumni && (
                            <div className="text-[10px] mt-1 inline-block px-1.5 py-0.5 rounded bg-[#EDE9FE] text-[#5B21B6] font-medium">⭐ {a.elite_university}</div>
                          )}
                        </div>
                      ) : <span className="text-[#9CA3AF]">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {a.alumni_current_profession && <div className="text-[#1F2937]">{a.alumni_current_profession}</div>}
                      {a.alumni_current_company && <div className="text-xs text-[#4B5563]">Kini: {a.alumni_current_company}</div>}
                      {a.alumni_previous_company && <div className="text-xs text-[#9CA3AF]">Sebelumnya: {a.alumni_previous_company}</div>}
                      {!a.alumni_current_profession && !a.alumni_current_company && !a.alumni_previous_company && <span className="text-[#9CA3AF]">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <StudyPill k={a.study_destination} />
                    </td>
                    <td className="px-4 py-3">
                      <CareerPill k={a.career_bucket} />
                    </td>
                    <td className="px-4 py-3">
                      <button data-testid={`alumni-edit-${i}`} title="Rapikan data alumni"
                        onClick={() => setEditing({ ...a })}
                        className="p-2 rounded-full hover:bg-[#EFF6FF] text-[#1E3A8A]">
                        <PencilLine size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {(!filteredList || filteredList.length === 0) && !loading && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-[#4B5563] italic">Belum ada data alumni untuk filter ini.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <EditDialog editing={editing} setEditing={setEditing} onSave={savePatch} saving={saving} />}
    </div>
  );
}

function KpiCard({ label, value, sub, accent = "#1F2937", testid }) {
  return (
    <div className="bg-white border border-[#EAE7E0] rounded-2xl p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#4B5563]">{label}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <div className="font-serif-display text-3xl" style={{ color: accent }}>{value}</div>
        {sub && <div className="text-xs text-[#4B5563]">{sub}</div>}
      </div>
    </div>
  );
}

function StudyPill({ k }) {
  const map = {
    elite: { label: "Elite", bg: "#EDE9FE", fg: "#5B21B6" },
    ptn: { label: "PTN", bg: "#DBEAFE", fg: "#1E40AF" },
    luar_negeri: { label: "Luar Negeri", bg: "#E0F2FE", fg: "#075985" },
    pts_unknown: { label: "PTS/?", bg: "#F3F4F6", fg: "#4B5563" },
  };
  const c = map[k] || map.pts_unknown;
  return <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ background: c.bg, color: c.fg }}>{c.label}</span>;
}

function CareerPill({ k }) {
  const map = {
    pengusaha: { label: "Pengusaha", bg: "#FEF3C7", fg: "#92400E" },
    profesional: { label: "Profesional", bg: "#DBEAFE", fg: "#1E40AF" },
    freelancer: { label: "Freelancer", bg: "#FEF9C3", fg: "#854D0E" },
    lainnya: { label: "Lainnya", bg: "#F3F4F6", fg: "#4B5563" },
  };
  const c = map[k] || map.lainnya;
  return <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ background: c.bg, color: c.fg }}>{c.label}</span>;
}

function EditDialog({ editing, setEditing, onSave, saving }) {
  const field = (label, key, placeholder = "") => (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">{label}</div>
      <Input value={editing[key] || ""} placeholder={placeholder}
        onChange={(e) => setEditing((p) => ({ ...p, [key]: e.target.value }))}
        data-testid={`edit-${key}`} className="rounded-xl h-9" />
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      data-testid="alumni-edit-dialog"
      onClick={() => setEditing(null)}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-auto p-6 space-y-3">
        <div>
          <h3 className="font-serif-display text-2xl text-[#1F2937]">Rapikan data alumni</h3>
          <p className="text-xs text-[#4B5563]">Review #{editing.review_id} — {editing.user_name}. Perbaiki penulisan universitas/perusahaan untuk konsistensi laporan.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {field("Tahun lulus", "graduation_year")}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Jenjang tertinggi</div>
            <Select value={editing.alumni_highest_edu || "__none"}
              onValueChange={(v) => setEditing((p) => ({ ...p, alumni_highest_edu: v === "__none" ? "" : v }))}>
              <SelectTrigger className="rounded-xl h-9" data-testid="edit-alumni_highest_edu"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">—</SelectItem>
                <SelectItem value="D3">D3</SelectItem>
                <SelectItem value="S1">S1</SelectItem>
                <SelectItem value="S2">S2</SelectItem>
                <SelectItem value="S3">S3</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {field("Universitas D3", "alumni_edu_d3")}
          {field("Universitas S1", "alumni_edu_s1")}
          {field("Universitas S2", "alumni_edu_s2")}
          {field("Universitas S3", "alumni_edu_s3")}
          {field("Studi lanjutan (legacy)", "alumni_next_study")}
          {field("Profesi / Jabatan", "alumni_current_profession")}
          {field("Perusahaan saat ini", "alumni_current_company")}
          {field("Perusahaan sebelumnya", "alumni_previous_company")}
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-[#EAE7E0]">
          <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}
            className="rounded-full">Batal</Button>
          <Button onClick={onSave} disabled={saving} data-testid="alumni-edit-save"
            className="rounded-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90">
            {saving ? "Menyimpan…" : "Simpan"}
          </Button>
        </div>
      </div>
    </div>
  );
}
